import axios from 'axios';
import {jwtDecode} from "jwt-decode";

// Configure API base URL
// const API_BASE_URL = 'http://localhost:5001/api'; // Replace with your actual API URL
const API_BASE_URL = 'https://api.ankiverse.com/api/conversation';

// const API_BASE_URL = 'https://ecf9-130-245-192-1.ngrok-free.app/micro/api'; // Replace with your actual API URL

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Helper: Decode token and return its expiration time (in ms)
function getTokenExpiry(token) {
  try {
    const decoded = jwtDecode(token);
    // JWT exp is in seconds; convert to ms
    return decoded.exp * 1000;
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
}

let refreshTimeout;

// Schedule a proactive token refresh before expiry
function scheduleRefresh() {
  const token = localStorage.getItem("accessToken");
  if (!token) return;
  const expiryTime = getTokenExpiry(token);
  if (!expiryTime) return;

  const now = Date.now();
  const timeUntilExpiry = expiryTime - now;
  // Refresh 1 minute (60000ms) before expiry
  const refreshDelay = timeUntilExpiry - 60000;

  // If refreshDelay is negative, refresh immediately
  if (refreshDelay <= 0) {
    refreshAccessToken();
  } else {
    // Clear any existing timeout and schedule a new one
    if (refreshTimeout) clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(refreshAccessToken, refreshDelay);
  }
}

// Function to refresh the access token
async function refreshAccessToken() {
  try {
    const { data } = await axios.post(
      "http://localhost:5000/api/auth/refresh-token",
      {},
      { withCredentials: true }
    );
    localStorage.setItem("accessToken", data.accessToken);
    api.defaults.headers.common["Authorization"] = `Bearer ${data.accessToken}`;
    // Reschedule the proactive refresh with the new token
    scheduleRefresh();
  } catch (err) {
    console.error("Proactive token refresh failed", err);
    localStorage.removeItem("accessToken");
    window.location.href = "/login";
  }
}

// Request interceptor: attach the access token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: if a 401 error occurs, attempt a token refresh once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Check that error.response exists before trying to access status
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const { data } = await axios.post(
          "http://localhost:5000/api/auth/refresh-token",
          {},
          { withCredentials: true }
        );
        localStorage.setItem("accessToken", data.accessToken);
        api.defaults.headers.common["Authorization"] = `Bearer ${data.accessToken}`;
        // Reschedule proactive refresh after the token update
        scheduleRefresh();
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        localStorage.removeItem("accessToken");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// If an access token exists on load, schedule its refresh
if (localStorage.getItem("accessToken")) {
  scheduleRefresh();
}

// Utility function for API calls with retry mechanism
const withRetry = async (apiCall, maxRetries = 3) => {
  let lastError = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      console.warn(`API call failed (attempt ${attempt + 1}/${maxRetries}):`, error);
      
      // If this wasn't the last attempt, wait before retrying
      if (attempt < maxRetries - 1) {
        // Exponential backoff: 1s, 2s, 4s...
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // If all retries failed, throw the last error
  throw lastError;
};

// Conversation API methods with retry
const conversationAPI = {
  // Category methods
  getCategories: (params = {}) => 
    withRetry(() => api.get('/conversation/categories', { params })),
    
  getCategory: (categoryId) => 
    withRetry(() => api.get(`/conversation/categories/${categoryId}`)),
    
  getTemplatesByCategory: (categoryId, params = {}) => 
    withRetry(() => api.get(`/conversation/categories/${categoryId}/templates`, { params })),
  
  // Template methods
  getTemplates: (params = {}) => 
    withRetry(() => api.get('/conversation/templates', { params })),
  
  // User sessions
  getUserSessions: (userId, params = {}) => 
    withRetry(() => api.get(`/conversation/sessions/${userId}`, { params })),
    
  getSession: (sessionId) => 
    withRetry(() => api.get(`/conversation/session/${sessionId}`)),
  
  // Start new conversation
  startConversation: (data) => 
    withRetry(() => api.post('/conversation/start', data)),
  
  // Send message - this is the most critical one for retry
  sendMessage: (data) => 
    withRetry(() => api.post('/conversation/message', data)),
  
  // Complete conversation
  completeConversation: (sessionId) => 
    withRetry(() => api.put(`/conversation/complete/${sessionId}`))
};

export default conversationAPI;